(() => {
  if (customElements.get('joffz-product-sticky')) return;
  const owners = new WeakMap();
  customElements.define('joffz-product-sticky', class extends HTMLElement {
    connectedCallback() {
      if (this.ready || this.closest('quick-add-modal, quick-view-modal')) return;
      this.scope = document.getElementById(`ProductInfo-${this.dataset.section}`);
      this.form = document.getElementById(`product-form-${this.dataset.section}`);
      this.nativeButton = document.getElementById(`ProductSubmitButton-${this.dataset.section}`);
      if (!this.scope || !this.form || !this.nativeButton) return;
      this.button = this.querySelector('[data-sticky-add]');
      this.label = this.querySelector('[data-sticky-label]');
      this.price = this.querySelector('[data-sticky-price]');
      this.variant = this.querySelector('[data-sticky-variant]');
      this.options = this.querySelector('[data-sticky-options]');
      this.image = this.querySelector('.joffz-product-sticky__image');
      if (!this.button || !this.label || !this.price || !this.variant || !this.options) return;
      if (owners.has(this.form) && owners.get(this.form) !== this) {
        this.hidden = true;
        return;
      }
      owners.set(this.form, this);
      this.ready = true;
      this.nativePrice = document.getElementById(`price-${this.dataset.section}`);
      this.sync = this.sync.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onVariant = event => {
        if (event.detail?.sectionId !== this.dataset.section) return;
        this.selectedVariant = event.detail.variant;
        this.sync();
      };
      this.onError = event => {
        if (event.detail?.source !== this.dataset.section) return;
        this.scrollTo(this.nativeButton);
        this.nativeButton.focus({preventScroll: true});
      };
      this.visibilityFrame = 0;
      this.scheduleVisibility = () => {
        if (this.visibilityFrame) return;
        this.visibilityFrame = requestAnimationFrame(() => {
          this.visibilityFrame = 0;
          this.syncVisibility();
        });
      };
      this.addEventListener('click', this.onClick);
      this.scope.addEventListener('change', this.sync);
      document.addEventListener('variant:change', this.onVariant);
      document.addEventListener('cart:error', this.onError);
      window.addEventListener('scroll', this.scheduleVisibility, {passive: true});
      window.addEventListener('resize', this.scheduleVisibility, {passive: true});
      this.observer = new MutationObserver(this.sync);
      this.observer.observe(this.nativeButton, {attributes: true, childList: true, subtree: true, characterData: true});
      if (this.nativePrice) this.observer.observe(this.nativePrice, {attributes: true, childList: true, subtree: true, characterData: true});
      this.intersection = new IntersectionObserver(() => this.syncVisibility(), {threshold: 0});
      this.intersection.observe(this.nativeButton);
      this.sync();
      this.syncVisibility();
    }
    syncVisibility() {
      if (!this.ready) return;
      const rect = this.nativeButton.getBoundingClientRect();
      // Appear only after the original purchase button has scrolled out above.
      const hidden = !this.nativeButton.getClientRects().length || rect.height === 0 || rect.bottom > 0;
      if (hidden === this.hidden) return;
      const hadFocus = this.contains(document.activeElement);
      this.hidden = hidden;
      if (hidden && hadFocus) this.nativeButton.focus({preventScroll: true});
    }
    scrollTo(element) {
      element.scrollIntoView({block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'});
    }
    unavailable() {
      return this.nativeButton.disabled || this.nativeButton.getAttribute('aria-disabled') === 'true' || this.nativeButton.classList.contains('loading');
    }
    syncImage() {
      if (!this.image) return;
      const picker = this.scope.querySelector('variant-selects, variant-radios');
      const selected = picker && 'currentVariant' in picker ? picker.currentVariant : this.selectedVariant;
      // Before the picker initializes, preserve the server-rendered selected image.
      if (selected === undefined) return;
      const media = selected?.featured_media?.preview_image || selected?.featured_image;
      const source = (typeof media === 'string' ? media : media?.src) || this.dataset.defaultImage;
      if (!source) return;
      const url = new URL(source, document.baseURI);
      url.searchParams.set('width', '120');
      if (this.image.getAttribute('src') !== url.href) {
        this.image.removeAttribute('srcset');
        this.image.setAttribute('src', url.href);
      }
      this.image.alt = media?.alt || this.dataset.imageAlt || '';
    }
    sync() {
      const busy = this.nativeButton.classList.contains('loading');
      this.button.disabled = this.unavailable();
      this.button.setAttribute('aria-busy', String(busy));
      const label = this.nativeButton.querySelector('span:not(.hidden)')?.textContent.trim() || this.dataset.addLabel;
      if (this.label.textContent !== label) this.label.textContent = label;
      const priceContainer = this.nativePrice?.querySelector('.price');
      const priceSource = priceContainer?.querySelector(priceContainer.classList.contains('price--on-sale') ? '.price-item--sale' : '.price__regular .price-item--regular');
      const price = priceSource?.textContent.trim();
      if (price && this.price.textContent !== price) this.price.textContent = price;
      const choices = [...this.scope.querySelectorAll('variant-selects fieldset, variant-radios fieldset')].map(fieldset => fieldset.querySelector('select')?.value || fieldset.querySelector('input:checked')?.value).filter(Boolean).join(' / ');
      if (this.variant.textContent !== choices) this.variant.textContent = choices;
      this.options.hidden = !choices;
      this.syncImage();
    }
    onClick(event) {
      if (event.target.closest('[data-sticky-options]')) {
        const picker = this.scope.querySelector('variant-selects, variant-radios');
        if (!picker) return;
        this.scrollTo(picker);
        picker.querySelector('button[aria-checked=true], select, input:checked')?.focus({preventScroll: true});
      }
      if (!event.target.closest('[data-sticky-add]')) return;
      if (this.unavailable()) return;
      // Reuse the original form, including quantity, uploaded photo and validation.
      this.nativeButton.click();
      this.sync();
    }
    disconnectedCallback() {
      if (!this.ready) return;
      if (owners.get(this.form) === this) owners.delete(this.form);
      this.observer?.disconnect();
      this.intersection?.disconnect();
      this.removeEventListener('click', this.onClick);
      this.scope?.removeEventListener('change', this.sync);
      document.removeEventListener('variant:change', this.onVariant);
      document.removeEventListener('cart:error', this.onError);
      window.removeEventListener('scroll', this.scheduleVisibility);
      window.removeEventListener('resize', this.scheduleVisibility);
      cancelAnimationFrame(this.visibilityFrame);
      this.visibilityFrame = 0;
      this.ready = false;
    }
  });
})();
